/** @velyn-custom BEGIN — vely-pdp-hero.js (Option B bundle submit, CTA summary, rotator, description, availability UI) */

const BUNDLE_INPUT_CLASS = "vely-pdp-bundle-line";

/**
 * @velyn-custom — currency consistency fix.
 * ATC price/currency logic and math are unchanged; only how the price string
 * gets built changes.
 *
 * This file loads as a bare `type="module"` script with no import of the
 * theme's real `NextSkyTheme` (assets/global.js) — `NextSkyTheme` is never a
 * true global here, so `NextSkyTheme.formatMoney(...)` always silently fell
 * through to a bare `(cents/100).toFixed(2)` (no currency, period decimal),
 * which is exactly what overwrote the correct SSR-rendered price moments
 * after page load. applyMoneyFormat() below re-implements Shopify's standard
 * money-format substitution locally so this no longer depends on that
 * missing global (same output as assets/global.js's formatMoney for a given
 * format string).
 *
 * Prefer the raw shop.money_format exposed via [data-money-format] (same
 * source the initial SSR `{{ price | money }}` render uses in
 * snippets/vely-pdp-buy-buttons.liquid) over themeGlobalVariables.money_format,
 * which carries a separate EU-suffix / currency-code override that made the
 * post-interaction (variant/qty/accessory) price re-render show a different
 * currency style than the page's initial load.
 */
function getShopMoneyFormat(context) {
  if (!(context instanceof Element)) return null;

  const el = context.matches("[data-money-format]")
    ? context
    : context.querySelector("[data-money-format]") ||
      context.closest("[data-money-format]");
  return el?.getAttribute("data-money-format") || null;
}

function applyMoneyFormat(cents, format) {
  const value = Number(cents) || 0;
  const placeholderMatch = String(format).match(
    /\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/
  );
  const placeholder = placeholderMatch ? placeholderMatch[1] : "amount";

  let precision = 2;
  let thousands = ",";
  let decimal = ".";
  switch (placeholder) {
    case "amount_no_decimals":
      precision = 0;
      break;
    case "amount_with_comma_separator":
      thousands = ".";
      decimal = ",";
      break;
    case "amount_no_decimals_with_comma_separator":
      precision = 0;
      thousands = ".";
      decimal = ",";
      break;
    case "amount_with_apostrophe_separator":
      thousands = "'";
      break;
    case "amount_no_decimals_with_space_separator":
      precision = 0;
      thousands = " ";
      break;
    case "amount_with_space_separator":
      thousands = " ";
      decimal = ",";
      break;
    case "amount_with_period_and_space_separator":
      thousands = " ";
      break;
  }

  const negative = value < 0;
  const base = (Math.abs(value) / 100).toFixed(precision);
  const [integerPart, fractionPart = ""] = base.split(".");
  const withThousands = integerPart.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    thousands
  );
  const formatted =
    (negative ? "-" : "") +
    withThousands +
    (precision > 0 ? decimal + fractionPart : "");

  return String(format).replace(
    /\{\{\s*amount(?:_[a-z_]+)?\s*\}\}/,
    formatted
  );
}

function formatMoney(cents, context) {
  const format =
    getShopMoneyFormat(context) ||
    window.themeGlobalVariables?.settings?.money_format ||
    window.cartStrings?.money_format ||
    // Last-resort: any [data-money-format] on the page (always shop.money_format,
    // rendered by every vely-pdp-buy-buttons.liquid instance) — guarantees a
    // currency symbol is never dropped even if `context` is missing/unscoped.
    document.querySelector("[data-money-format]")?.getAttribute("data-money-format") ||
    "{{amount}}";

  return applyMoneyFormat(cents, format);
}

function getProductForms(section) {
  return Array.from(
    section.querySelectorAll("product-form .main-product-form")
  );
}

function removeBundleLines(form) {
  form.querySelectorAll(`.${BUNDLE_INPUT_CLASS}`).forEach((el) => el.remove());
}

function restorePrimaryIdField(form) {
  const disabled = form.querySelector("[data-vely-primary-id]");
  if (!disabled) return;
  disabled.setAttribute("name", "id");
  disabled.removeAttribute("data-vely-primary-id");
}

function appendBundleLine(form, variantId, quantity) {
  const idInput = document.createElement("input");
  idInput.type = "hidden";
  idInput.name = "items[][id]";
  idInput.value = variantId;
  idInput.className = BUNDLE_INPUT_CLASS;

  const qtyInput = document.createElement("input");
  qtyInput.type = "hidden";
  qtyInput.name = "items[][quantity]";
  qtyInput.value = String(quantity);
  qtyInput.className = BUNDLE_INPUT_CLASS;

  form.appendChild(idInput);
  form.appendChild(qtyInput);
}

function getPrimaryVariantInput(form) {
  return (
    form.querySelector('[name="id"]') ||
    form.querySelector("[data-vely-primary-id]") ||
    form.querySelector("input.product-variant-id")
  );
}

function getCheckedAccessories(section) {
  return Array.from(
    section.querySelectorAll(".vely-pdp-accessory__checkbox:checked")
  ).map((cb) => ({
    variantId: cb.dataset.variantId || cb.value,
    price: Number(cb.dataset.price) || 0,
    compareAt: Number(cb.dataset.compareAt) || 0,
  }));
}

function prepareBundleSubmit(form, section) {
  const accessories = getCheckedAccessories(section);
  if (accessories.length === 0) return false;

  removeBundleLines(form);
  restorePrimaryIdField(form);

  const primaryId = getPrimaryVariantInput(form);
  const qtyInput = form.querySelector('[name="quantity"]');
  if (!primaryId?.value) return false;

  const quantity = qtyInput?.value || "1";

  primaryId.setAttribute("data-vely-primary-id", "true");
  primaryId.removeAttribute("name");

  appendBundleLine(form, primaryId.value, quantity);
  accessories.forEach((a) => {
    if (a.variantId) appendBundleLine(form, a.variantId, 1);
  });

  return true;
}

function getAtcPriceElements(section) {
  return Array.from(
    section.querySelectorAll(
      "[data-vely-atc-price], .block-product__buttons .total-price__detail, .sticky-add-cart .total-price__detail"
    )
  );
}

/**
 * Main product unit price only (never includes accessories).
 * Prefer data-vely-main-price so data-total-price can safely hold the combined total.
 */
function getBaseMainPriceCents(section) {
  const priceEl = section.querySelector("[data-vely-atc-price]");
  const fromAttr = Number(priceEl?.getAttribute("data-vely-main-price"));
  if (Number.isFinite(fromAttr) && fromAttr > 0) return fromAttr;

  const fromDataset = Number(section.dataset.velyMainVariantPrice);
  if (Number.isFinite(fromDataset) && fromDataset > 0) return fromDataset;

  return 0;
}

function setBaseMainPriceCents(section, cents) {
  const value = Number(cents);
  if (!Number.isFinite(value) || value < 0) return;

  section.dataset.velyMainVariantPrice = String(value);
  getAtcPriceElements(section).forEach((priceEl) => {
    priceEl.setAttribute("data-vely-main-price", String(value));
  });
}

function getSelectedQuantity(section) {
  const qtyInput = section.querySelector(
    '.block-product__buttons [name="quantity"], .product-detail__buy-buttons [name="quantity"], .sticky-add-cart [name="quantity"]'
  );
  const qty = Number(qtyInput?.value);
  return Number.isFinite(qty) && qty > 0 ? qty : 1;
}

function getAccessoriesTotalCents(section) {
  return getCheckedAccessories(section).reduce(
    (sum, item) => sum + (Number(item.price) || 0),
    0
  );
}

function getAccessoriesSaveCents(section) {
  return getCheckedAccessories(section).reduce((sum, item) => {
    const price = Number(item.price) || 0;
    const compare = Number(item.compareAt) || 0;
    const base = compare > price ? compare : price;
    return sum + Math.max(0, base - price);
  }, 0);
}

function computeAtcTotalCents(section) {
  return (
    getBaseMainPriceCents(section) * getSelectedQuantity(section) +
    getAccessoriesTotalCents(section)
  );
}

function getAtcLabels(section) {
  return Array.from(
    section.querySelectorAll(
      ".product_submit_button .vely-pdp-atc__label, .sticky-add-cart .product_submit_button .vely-pdp-atc__label"
    )
  );
}

function formatSavingsLabel(template, saveCents, section) {
  if (!template) return "";
  return template
    .replace(/\{\{\s*amount\s*\}\}/gi, formatMoney(saveCents, section))
    .replace(/\[save\]/gi, formatMoney(saveCents, section));
}

function clearAtcLoadingState(section) {
  section.querySelectorAll(".product_submit_button.loading").forEach((btn) => {
    if (btn.getAttribute("aria-disabled") !== "true") {
      btn.classList.remove("loading");
    }
  });
}

/**
 * Visible ATC amount = (main × qty) + selected accessories.
 *
 * data-total-price stays MAIN UNIT ONLY so theme QuantityInput
 * (data-total-price × qty) does not double-count accessories.
 * data-vely-main-price mirrors that main unit.
 * data-vely-combined-total stores main + accessories (no qty) for debugging.
 */
function updateCtaSummary(section) {
  if (!section) return;

  const priceEls = getAtcPriceElements(section);
  const labels = getAtcLabels(section);
  if (!priceEls.length && !labels.length) return;

  // Persist main unit from Liquid attrs if JS state is empty.
  if (!section.dataset.velyMainVariantPrice) {
    const priceEl = section.querySelector("[data-vely-atc-price]");
    const fromMain = Number(priceEl?.getAttribute("data-vely-main-price"));
    const fromTotal = Number(priceEl?.getAttribute("data-total-price"));
    const boot =
      Number.isFinite(fromMain) && fromMain > 0
        ? fromMain
        : Number.isFinite(fromTotal) && fromTotal > 0
          ? fromTotal
          : 0;
    if (boot > 0) setBaseMainPriceCents(section, boot);
  }

  const accessoriesHost = section.querySelector("vely-pdp-accessories");
  const dynamicCtaEnabled = accessoriesHost?.dataset.dynamicCta === "true";
  const baseLabel =
    accessoriesHost?.dataset.ctaBaseLabel ||
    section.dataset.addToCartLabel ||
    window.cartStrings?.vely_add_to_cart ||
    window.cartStrings?.add_to_cart ||
    "";
  const savingsTemplate =
    section.dataset.velyAtcSavingsTemplate ||
    window.cartStrings?.vely_add_to_cart_savings ||
    "";

  const accessories = getCheckedAccessories(section);
  const mainUnit = getBaseMainPriceCents(section);
  const accessoriesTotal = getAccessoriesTotalCents(section);
  const displayCents = computeAtcTotalCents(section);
  const saveCents = getAccessoriesSaveCents(section);
  const showPrice = displayCents > 0;
  const showSavings =
    !isVelyPdpMobile() &&
    dynamicCtaEnabled &&
    accessories.length > 0 &&
    saveCents > 0;
  const savingsText = showSavings
    ? formatSavingsLabel(savingsTemplate, saveCents, section)
    : "";
  const moneyText = showPrice ? formatMoney(displayCents, section) : "";
  const mainAttr = String(Math.round(mainUnit));
  const combinedUnitAttr = String(Math.round(mainUnit + accessoriesTotal));

  priceEls.forEach((priceEl) => {
    if (mainUnit > 0) {
      priceEl.setAttribute("data-vely-main-price", mainAttr);
      // Theme qty handler: data-total-price × qty — must be main unit only.
      priceEl.setAttribute("data-total-price", mainAttr);
    }
    priceEl.setAttribute("data-vely-combined-total", combinedUnitAttr);
    if (showPrice) {
      priceEl.textContent = moneyText;
    }
  });

  labels.forEach((labelEl) => {
    const baseSpan = labelEl.querySelector(".vely-pdp-atc__base");
    const savingsSpan = labelEl.querySelector(".vely-pdp-atc__savings");
    const totalWrap = labelEl.querySelector(
      ".vely-pdp-atc__total, [data-vely-atc-total]"
    );
    const displayTotal = labelEl.querySelector(
      "[data-vely-atc-display-total], [data-vely-atc-price], .total-price__detail"
    );

    if (baseSpan && baseLabel && baseSpan.textContent !== baseLabel) {
      baseSpan.textContent = baseLabel;
    }

    if (displayTotal && showPrice) {
      displayTotal.textContent = moneyText;
      if (mainUnit > 0) {
        displayTotal.setAttribute("data-vely-main-price", mainAttr);
        displayTotal.setAttribute("data-total-price", mainAttr);
      }
      displayTotal.setAttribute("data-vely-combined-total", combinedUnitAttr);
    }

    if (totalWrap) {
      totalWrap.hidden = !showPrice;
      totalWrap.setAttribute("aria-hidden", showPrice ? "false" : "true");
    }

    if (!savingsSpan) return;

    if (showSavings && savingsText) {
      savingsSpan.textContent = savingsText;
      savingsSpan.hidden = false;
      savingsSpan.setAttribute("aria-hidden", "false");
    } else {
      savingsSpan.textContent = "";
      savingsSpan.hidden = true;
      savingsSpan.setAttribute("aria-hidden", "true");
    }
  });

  clearAtcLoadingState(section);
}

function getProductVariants(section) {
  const script = section.querySelector("[data-vely-product-variants]");
  if (!script) return [];
  try {
    const parsed = JSON.parse(script.textContent);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
}

function resolveSelectedVariantFromRadios(section) {
  const checked = Array.from(
    section.querySelectorAll(
      ".product-card-variant-js input[type='radio']:checked"
    )
  );
  if (!checked.length) return null;

  const options = {};
  checked.forEach((radio) => {
    const position = Number(radio.dataset.optionPosition);
    if (position >= 1 && position <= 3) {
      options[position] = radio.dataset.optionValue;
    }
  });

  const variants = getProductVariants(section);
  return (
    variants.find((variant) => {
      if (options[1] != null && variant.option1 !== options[1]) return false;
      if (options[2] != null && variant.option2 !== options[2]) return false;
      if (options[3] != null && variant.option3 !== options[3]) return false;
      return true;
    }) || null
  );
}

function syncVariantIdInputs(section, variantId, variantPrice) {
  if (!variantId || section._velyVariantSyncing) return;
  section._velyVariantSyncing = true;

  try {
    const nextId = String(variantId);
    section
      .querySelectorAll(
        'input.product-variant-id, input[name="id"], input[data-vely-primary-id]'
      )
      .forEach((input) => {
        if (input.value !== nextId) input.value = nextId;
        if (input.disabled) input.disabled = false;
        if (
          input.hasAttribute("data-vely-primary-id") ||
          !input.getAttribute("name")
        ) {
          input.setAttribute("name", "id");
          input.removeAttribute("data-vely-primary-id");
        }
      });

    // Only store main unit price here. Combined ATC total is written by updateCtaSummary.
    if (variantPrice != null && Number(variantPrice) >= 0) {
      setBaseMainPriceCents(section, variantPrice);
    }
  } finally {
    queueMicrotask(() => {
      section._velyVariantSyncing = false;
    });
  }
}

function isMainVariantSoldOut(section) {
  if (section.dataset.productAvailable === "false") return true;

  const submit = section.querySelector(
    ".block-product__buttons .product_submit_button"
  );
  if (!submit?.hasAttribute("disabled")) return false;

  return !submit.querySelector("[data-vely-atc-price], .total-price__detail");
}

function isVelyPdpMobile() {
  return window.matchMedia("(max-width: 767.98px)").matches;
}

function syncAvailabilityUi(section) {
  const unavailable = isMainVariantSoldOut(section);

  section.classList.toggle("vely-pdp--variant-unavailable", unavailable);

  const rotator = section.querySelector("vely-pdp-rotator");
  const rotatorRoot = section.querySelector(".block-product__vely-rotator-root");
  if (rotator && rotator.dataset.hideWhenUnavailable === "true") {
    rotator.hidden = unavailable;
  }
  if (rotatorRoot && rotator?.dataset.hideWhenUnavailable === "true") {
    rotatorRoot.hidden = unavailable;
  }

  section
    .querySelectorAll(
      ".block-product__vely-accessories-root, .block-product__vely-promo, .vely-pdp-promo"
    )
    .forEach((el) => {
      if (unavailable) el.setAttribute("hidden", "");
      else el.removeAttribute("hidden");
    });
}

function syncAccessoryCardVisualState(card) {
  const checkbox = card?.querySelector(".vely-pdp-accessory__checkbox");
  if (!checkbox) return;
  card.classList.toggle("is-selected", checkbox.checked);
  card.classList.toggle("is-unavailable", checkbox.disabled);
}

function parseDatasetBoolean(value, defaultValue = false) {
  if (value == null || value === "") return defaultValue;
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "true" || normalized === "1") return true;
  if (normalized === "false" || normalized === "0") return false;
  return defaultValue;
}

function syncAccessoryCheckboxAvailability(card) {
  const checkbox = card?.querySelector(".vely-pdp-accessory__checkbox");
  if (!checkbox) return;

  const selectedSwatch = card.querySelector("[data-acc-swatch].is-selected");
  let available = parseDatasetBoolean(checkbox.dataset.available, true);

  if (selectedSwatch?.dataset.available != null) {
    available = parseDatasetBoolean(selectedSwatch.dataset.available, available);
  }

  checkbox.disabled = !available;
  if (!available) checkbox.checked = false;
  syncAccessoryCardVisualState(card);
}

function parsePositivePercent(value) {
  if (value == null || value === "") return 0;
  const normalized = String(value).trim().replace(/%/g, "");
  const num = Number(normalized);
  if (!Number.isFinite(num) || num <= 0) return 0;
  return Math.round(num);
}

function getAccessorySalePercent(card) {
  const selectedSwatch = card.querySelector("[data-acc-swatch].is-selected");
  if (selectedSwatch?.dataset.salePercent != null) {
    const fromSwatch = parsePositivePercent(selectedSwatch.dataset.salePercent);
    if (fromSwatch > 0) return fromSwatch;
  }

  const checkbox = card.querySelector(".vely-pdp-accessory__checkbox");
  if (checkbox?.dataset.salePercent != null) {
    const fromCheckbox = parsePositivePercent(checkbox.dataset.salePercent);
    if (fromCheckbox > 0) return fromCheckbox;
  }

  const fromCard = parsePositivePercent(card.dataset.salePercent);
  if (fromCard > 0) return fromCard;

  if (checkbox) {
    const price = Number(checkbox.dataset.price) || 0;
    const compareAt = Number(checkbox.dataset.compareAt) || 0;
    if (compareAt > price) {
      return Math.round(((compareAt - price) / compareAt) * 100);
    }
  }

  return 0;
}

function setAccessoryBadge(badge, show, text) {
  if (!badge) return;
  badge.textContent = show ? text : "";
  badge.hidden = !show;
  badge.classList.toggle("hidden", !show);
}

function updateAccessoryBadges(card, salePercent) {
  const badgesWrap = card.querySelector("[data-acc-badges]");
  const extraBadge = card.querySelector("[data-acc-extra-badge]");
  const saleBadge = card.querySelector("[data-acc-sale-badge]");
  const extraPercent = parsePositivePercent(card.dataset.extraDiscount);
  const sale = parsePositivePercent(salePercent);

  setAccessoryBadge(extraBadge, extraPercent > 0, `-${extraPercent}% Extra`);
  setAccessoryBadge(saleBadge, sale > 0, `-${sale}%`);

  if (badgesWrap) {
    const hasVisible =
      (extraBadge && !extraBadge.hidden) || (saleBadge && !saleBadge.hidden);
    badgesWrap.hidden = !hasVisible;
    badgesWrap.classList.toggle("hidden", !hasVisible);
  }
}

function normalizeAccessoryImageUrl(url) {
  if (!url) return "";
  const absolute = url.startsWith("//") ? `https:${url}` : url;
  try {
    return new URL(absolute, window.location.origin).href;
  } catch (_) {
    return absolute;
  }
}

function getAccessoryVariantImage(card, variantId, swatchBtn) {
  let imageUrl = "";
  let imageAlt = swatchBtn?.dataset.imageAlt || "";

  const variantsScript = card.querySelector("[data-acc-variants]");
  if (variantsScript && variantId) {
    try {
      const variants = JSON.parse(variantsScript.textContent);
      const variant = variants.find((v) => String(v.id) === String(variantId));
      if (variant?.featured_image?.src) {
        imageUrl = normalizeAccessoryImageUrl(variant.featured_image.src);
        if (variant.featured_image.alt) imageAlt = variant.featured_image.alt;
      } else if (variant?.featured_media?.preview_image?.src) {
        imageUrl = normalizeAccessoryImageUrl(variant.featured_media.preview_image.src);
      }
    } catch (_) {
      /* ignore invalid JSON */
    }
  }

  if (!imageUrl && swatchBtn?.dataset.imageUrl) {
    imageUrl = normalizeAccessoryImageUrl(swatchBtn.dataset.imageUrl);
  }

  return { imageUrl, imageAlt };
}

function updateAccessoryImage(card, swatchBtn, variantId) {
  const imageEl = card.querySelector(".vely-pdp-accessory__img");
  if (!imageEl || !variantId) return;

  const prevVariantId = imageEl.dataset.currentVariantId || imageEl.dataset.variantId || "";
  if (String(prevVariantId) === String(variantId)) return;

  const { imageUrl, imageAlt } = getAccessoryVariantImage(card, variantId, swatchBtn);
  if (!imageUrl) return;

  imageEl.dataset.currentVariantId = String(variantId);
  imageEl.dataset.currentSrc = imageUrl;

  const reveal = () => {
    imageEl.style.opacity = "1";
    imageEl.removeEventListener("load", reveal);
    imageEl.removeEventListener("error", reveal);
  };

  imageEl.style.opacity = "0.6";
  imageEl.addEventListener("load", reveal);
  imageEl.addEventListener("error", reveal);
  imageEl.src = imageUrl;
  if (imageAlt) imageEl.alt = imageAlt;
}

function initAccessoryCardImage(card) {
  const imageEl = card.querySelector(".vely-pdp-accessory__img");
  const selectedSwatch =
    card.querySelector("[data-acc-swatch].is-selected") ||
    card.querySelector("[data-acc-swatch]");
  if (!imageEl || !selectedSwatch) return;

  const variantId = selectedSwatch.dataset.variantId;
  if (variantId) {
    imageEl.dataset.currentVariantId = variantId;
    imageEl.dataset.variantId = variantId;
  }
  if (selectedSwatch.dataset.imageUrl) {
    imageEl.dataset.currentSrc = normalizeAccessoryImageUrl(
      selectedSwatch.dataset.imageUrl
    );
  } else if (imageEl.src) {
    imageEl.dataset.currentSrc = normalizeAccessoryImageUrl(imageEl.src);
  }
}

function bindAccessorySwatches(section) {
  section.querySelectorAll("[data-acc-swatch]").forEach((swatchBtn) => {
    if (swatchBtn.dataset.velyAccSwatchBound === "true") return;
    swatchBtn.dataset.velyAccSwatchBound = "true";

    swatchBtn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const card = swatchBtn.closest("[data-acc-card]");
      if (card) updateAccessoryVariant(card, swatchBtn, section);
      requestAnimationFrame(() => swatchBtn.blur());
    });
  });
}

function updateAccessoryVariant(card, swatchBtn, section) {
  const checkbox = card.querySelector(".vely-pdp-accessory__checkbox");
  if (!checkbox) return;

  const variantId = swatchBtn.dataset.variantId;
  const price = Number(swatchBtn.dataset.price) || 0;
  const compareAt = Number(swatchBtn.dataset.compareAt) || price;
  const available = parseDatasetBoolean(swatchBtn.dataset.available, true);
  const salePercent = parsePositivePercent(
    swatchBtn.dataset.salePercent != null
      ? swatchBtn.dataset.salePercent
      : compareAt > price
        ? Math.round(((compareAt - price) / compareAt) * 100)
        : 0
  );
  const currentPrice = card.querySelector("[data-acc-price]");
  const comparePrice = card.querySelector("[data-acc-compare]");

  checkbox.value = variantId;
  checkbox.dataset.variantId = variantId;
  checkbox.dataset.price = String(price);
  checkbox.dataset.compareAt = String(compareAt);
  checkbox.dataset.salePercent = String(salePercent);
  checkbox.dataset.available = available ? "true" : "false";
  checkbox.disabled = !available;
  if (!available) checkbox.checked = false;

  updateAccessoryImage(card, swatchBtn, variantId);

  if (currentPrice) {
    currentPrice.textContent = formatMoney(price, section);
  }
  if (comparePrice) {
    const hasCompare = compareAt > price;
    comparePrice.textContent = hasCompare ? formatMoney(compareAt, section) : "";
    comparePrice.classList.toggle("hidden", !hasCompare);
  }

  updateAccessoryBadges(card, salePercent);
  card.dataset.salePercent = String(salePercent);

  card.querySelectorAll("[data-acc-swatch]").forEach((el) => {
    const isCurrent = el === swatchBtn;
    el.classList.toggle("is-selected", isCurrent);
    el.setAttribute("aria-checked", isCurrent ? "true" : "false");
  });

  syncAccessoryCheckboxAvailability(card);
  updateCtaSummary(section);

  // Swatch clicks must not leave the ATC button in spinner/loading UI.
  section
    .querySelectorAll(".product_submit_button.loading")
    .forEach((btn) => {
      if (btn.getAttribute("aria-disabled") !== "true") {
        btn.classList.remove("loading");
      }
    });
}

function bindAccessoryChange(section) {
  if (section.dataset.velyAccessoriesBound === "true") return;
  section.dataset.velyAccessoriesBound = "true";

  section.querySelectorAll("[data-acc-card]").forEach((card) => {
    syncAccessoryCheckboxAvailability(card);
    updateAccessoryBadges(card, getAccessorySalePercent(card));
    initAccessoryCardImage(card);
  });

  bindAccessorySwatches(section);

  section.addEventListener("click", (event) => {
    const checkbox = event.target.closest(".vely-pdp-accessory__checkbox");
    if (checkbox) {
      if (checkbox.disabled) event.preventDefault();
      return;
    }

    const detailsTrigger = event.target.closest(".vely-pdp-accessory__details");
    if (detailsTrigger) {
      requestAnimationFrame(() => detailsTrigger.blur());
      return;
    }

    const card = event.target.closest("[data-acc-card]");
    if (!card || !section.contains(card)) return;

    if (event.target.closest("[data-acc-swatch]")) return;

    if (event.target.closest(".vely-pdp-accessory__check-wrap label")) return;

    const interactive = event.target.closest("button, a");
    if (interactive) return;

    const cardCheckbox = card.querySelector(".vely-pdp-accessory__checkbox");
    if (cardCheckbox && !cardCheckbox.disabled) {
      cardCheckbox.checked = !cardCheckbox.checked;
      cardCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  section.addEventListener("keydown", (event) => {
    const card = event.target.closest("[data-acc-card]");
    if (!card || !section.contains(card)) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    if (event.target.closest("button, a, input")) return;
    event.preventDefault();
    const checkbox = card.querySelector(".vely-pdp-accessory__checkbox");
    if (checkbox && !checkbox.disabled) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event("change", { bubbles: true }));
    }
  });

  const refreshAtcFromAccessory = () => {
    // Theme QuantityInput may rewrite .total-price__detail after change;
    // run once now and once after paint so accessories always win.
    updateCtaSummary(section);
    requestAnimationFrame(() => {
      updateCtaSummary(section);
      clearAtcLoadingState(section);
    });
  };

  section.addEventListener("change", (event) => {
    if (!event.target.matches(".vely-pdp-accessory__checkbox")) return;
    const card = event.target.closest("[data-acc-card]");
    if (card) syncAccessoryCardVisualState(card);
    refreshAtcFromAccessory();
  });

  section
    .querySelectorAll(
      '.block-product__buttons [name="quantity"], .product-detail__buy-buttons [name="quantity"], .sticky-add-cart [name="quantity"]'
    )
    .forEach((qty) => {
      qty.addEventListener("change", refreshAtcFromAccessory);
      qty.addEventListener("input", refreshAtcFromAccessory);
    });
}

function bindGlobalAccessoryAtcSync() {
  if (window.__velyAccessoryAtcSyncBound) return;
  window.__velyAccessoryAtcSyncBound = true;

  const findSection = (node) =>
    node?.closest?.("[data-vely-pdp-section]") ||
    document.querySelector("[data-vely-pdp-section]");

  document.addEventListener(
    "change",
    (event) => {
      if (!event.target.matches?.(".vely-pdp-accessory__checkbox")) return;
      const section = findSection(event.target);
      if (!section) return;
      const card = event.target.closest("[data-acc-card]");
      if (card) syncAccessoryCardVisualState(card);
      updateCtaSummary(section);
      requestAnimationFrame(() => {
        updateCtaSummary(section);
        clearAtcLoadingState(section);
      });
    },
    true
  );
}

function bindVariantPriceObservers(section) {
  // Intentionally no MutationObserver on data-total-price.
  // Writing the combined total there must not re-enter updateCtaSummary.
  if (Array.isArray(section._velyPriceObservers)) {
    section._velyPriceObservers.forEach((observer) => observer.disconnect());
  }
  section._velyPriceObservers = [];
}

function observeVariantPrice(section) {
  bindVariantPriceObservers(section);
}

function captureMainPriceFromAtcDom(section) {
  const priceEl = section.querySelector("[data-vely-atc-price]");
  if (!priceEl) return;

  // Prefer dedicated main-price attr from Liquid / last variant sync.
  const mainAttr = Number(priceEl.getAttribute("data-vely-main-price"));
  if (Number.isFinite(mainAttr) && mainAttr > 0) {
    setBaseMainPriceCents(section, mainAttr);
    return;
  }

  // Fresh Liquid markup only: data-total-price is still main-only.
  if (!section.dataset.velyMainVariantPrice) {
    const raw = Number(priceEl.getAttribute("data-total-price"));
    if (Number.isFinite(raw) && raw > 0) {
      setBaseMainPriceCents(section, raw);
    }
  }
}

function observeAtcDomRefresh(section) {
  if (section.dataset.velyAtcDomBound === "true") return;
  section.dataset.velyAtcDomBound = "true";

  const bindBuyButtonHosts = () => {
    section
      .querySelectorAll(
        ".block-product__buttons .button_buy-now, .sticky-add-cart .button_buy-now"
      )
      .forEach((host) => {
        if (host.dataset.velyAtcObserved === "true") return;
        host.dataset.velyAtcObserved = "true";

        const observer = new MutationObserver((mutations) => {
          const buttonReplaced = mutations.some(
            (mutation) =>
              mutation.type === "childList" && mutation.addedNodes.length > 0
          );
          if (!buttonReplaced) return;
          scheduleAtcRefresh();
        });
        observer.observe(host, { childList: true });
      });
  };

  const scheduleAtcRefresh = () => {
    if (section._velyAtcRefreshQueued) return;
    section._velyAtcRefreshQueued = true;
    requestAnimationFrame(() => {
      section._velyAtcRefreshQueued = false;
      bindBuyButtonHosts();
      captureMainPriceFromAtcDom(section);
      updateCtaSummary(section);
      syncAvailabilityUi(section);
    });
  };

  bindBuyButtonHosts();

  section.querySelectorAll(".sticky-sticky__buy-buttons").forEach((stickyHost) => {
    const observer = new MutationObserver(() => scheduleAtcRefresh());
    observer.observe(stickyHost, { childList: true });
  });
}

function bindForm(form, section) {
  if (!form || form.dataset.velyPdpBound === "true") return;
  form.dataset.velyPdpBound = "true";

  form.addEventListener(
    "submit",
    () => {
      removeBundleLines(form);
      restorePrimaryIdField(form);
      if (!section.querySelector("vely-pdp-accessories")) return;
      prepareBundleSubmit(form, section);
    },
    { capture: true }
  );
}

function bindInstantVariantSync(section) {
  if (section.dataset.velyVariantSyncBound === "true") return;
  section.dataset.velyVariantSyncBound = "true";

  section.addEventListener(
    "change",
    (event) => {
      if (section._velyVariantSyncing) return;
      if (!event.target.matches(".product-card-variant-js input[type='radio']")) {
        return;
      }

      const variant = resolveSelectedVariantFromRadios(section);
      if (!variant?.id) return;
      syncVariantIdInputs(section, variant.id, variant.price);
      updateCtaSummary(section);
    },
    true
  );

  if (!window.__velyVariantUpdatedBound) {
    window.__velyVariantUpdatedBound = true;
    document.addEventListener("vely:variant:updated", (event) => {
      const targetSection =
        event.detail?.section ||
        (event.detail?.sectionId
          ? document.getElementById(`Product-${event.detail.sectionId}`)
          : null);
      if (!targetSection?.hasAttribute("data-vely-pdp-section")) return;

      if (event.detail?.variantId) {
        syncVariantIdInputs(
          targetSection,
          event.detail.variantId,
          event.detail.variant?.price
        );
      }
      requestAnimationFrame(() => {
        updateCtaSummary(targetSection);
        syncAvailabilityUi(targetSection);
      });
    });
  }
}

function bindSection(section) {
  // Only bind the real PDP root, not the Shopify section wrapper class.
  if (!section?.hasAttribute("data-vely-pdp-section")) return;

  const forms = getProductForms(section);
  forms.forEach((form) => bindForm(form, section));

  if (section.dataset.velyPdpSectionBound === "true") return;
  section.dataset.velyPdpSectionBound = "true";

  const initialMain = getBaseMainPriceCents(section);
  if (initialMain > 0) setBaseMainPriceCents(section, initialMain);

  bindGlobalAccessoryAtcSync();
  bindAccessoryChange(section);
  bindInstantVariantSync(section);
  observeVariantPrice(section);
  observeAtcDomRefresh(section);

  const submit = section.querySelector(
    ".block-product__buttons .product_submit_button"
  );
  if (submit) {
    const submitObserver = new MutationObserver(() => syncAvailabilityUi(section));
    submitObserver.observe(submit, {
      attributes: true,
      attributeFilter: ["disabled"],
    });
  }

  section.addEventListener("change", (event) => {
    if (
      event.target.matches(
        '.vely-pdp-accessory__checkbox, [name="quantity"]'
      )
    ) {
      updateCtaSummary(section);
      syncAvailabilityUi(section);
    }
  });

  section.addEventListener("input", (event) => {
    if (event.target.matches('[name="quantity"]')) {
      requestAnimationFrame(() => updateCtaSummary(section));
    }
  });

  if (!window.__velyPdpCartRestoreBound) {
    window.__velyPdpCartRestoreBound = true;
    document.addEventListener("nextsky:cart:added", () => {
      document
        .querySelectorAll("[data-vely-pdp-section]")
        .forEach((pdpSection) => {
          getProductForms(pdpSection).forEach((form) => {
            restorePrimaryIdField(form);
            removeBundleLines(form);
          });
        });
    });
  }

  updateCtaSummary(section);
  syncAvailabilityUi(section);

  if (!section.dataset.velyAtcMqBound) {
    section.dataset.velyAtcMqBound = "true";
    const mq = window.matchMedia("(max-width: 767.98px)");
    const onMqChange = () => updateCtaSummary(section);
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", onMqChange);
    } else if (typeof mq.addListener === "function") {
      mq.addListener(onMqChange);
    }
  }
}

window.velyBindPdpSection = bindSection;

class VelyPdpRotator extends HTMLElement {
  connectedCallback() {
    if (this._velyRotatorInit) return;
    this._velyRotatorInit = true;

    const viewport = this.querySelector(".vely-pdp-rotator__viewport");
    const track = this.querySelector("[data-vely-rotator-track]");
    const lines = this.querySelectorAll("[data-vely-rotator-line]");
    if (!viewport || !track || lines.length === 0) return;

    const syncHeights = () => {
      const height = Math.max(
        ...Array.from(lines).map((line) => line.offsetHeight || 0),
        20
      );
      viewport.style.height = `${height}px`;
      lines.forEach((line) => {
        line.style.minHeight = `${height}px`;
      });
      return height;
    };

    let lineHeight = syncHeights();
    let index = 0;

    const goTo = (nextIndex) => {
      index = nextIndex;
      track.style.transform = `translate3d(0, ${-index * lineHeight}px, 0)`;
    };

    goTo(0);

    if (lines.length > 1) {
      const interval = Number(this.dataset.interval) || 5000;
      this._timer = window.setInterval(() => {
        goTo((index + 1) % lines.length);
      }, interval);
    }

    if (typeof ResizeObserver !== "undefined") {
      this._resizeObserver = new ResizeObserver(() => {
        lineHeight = syncHeights();
        goTo(index);
      });
      this._resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    if (this._timer) window.clearInterval(this._timer);
    this._resizeObserver?.disconnect();
  }
}

class VelyPdpDescription extends HTMLElement {
  connectedCallback() {
    if (this._velyDescriptionInit) return;
    this._velyDescriptionInit = true;

    const toggle = this.querySelector("[data-vely-description-toggle]");
    const content = this.querySelector("[data-vely-description-content]");
    const shell = this.querySelector(".vely-pdp-description__shell");
    if (!content) return;

    this._toggle = toggle;
    this._content = content;
    this._shell = shell;
    this._animToken = 0;

    const expandEnabled = this.dataset.expandEnabled === "true";
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const durationMs = reduceMotion ? 0 : 480;
    const ease = "cubic-bezier(0.22, 1, 0.36, 1)";

    const getLineHeight = () =>
      Math.ceil(parseFloat(getComputedStyle(content).lineHeight) || 22);

    const isOpen = () => toggle?.getAttribute("aria-expanded") === "true";

    const measureFullHeight = () => {
      const clone = content.cloneNode(true);
      clone.classList.remove(
        "vely-pdp-description__content--collapsed",
        "vely-pdp-description__content--expanded",
        "is-animating"
      );
      clone.style.cssText = [
        "position:absolute",
        "left:-9999px",
        "top:0",
        `width:${content.offsetWidth}px`,
        "max-height:none",
        "overflow:visible",
        "visibility:hidden",
        "pointer-events:none",
        "display:block",
        "-webkit-line-clamp:unset",
      ].join(";");
      this.appendChild(clone);
      const height = clone.scrollHeight;
      clone.remove();
      return height;
    };

    const syncToggleVisibility = () => {
      if (!toggle || !expandEnabled || this._animToken) return;
      const overflow = measureFullHeight() > getLineHeight() + 2;
      toggle.hidden = !overflow;
      if (!overflow) {
        content.classList.remove("vely-pdp-description__content--collapsed");
        content.classList.remove("vely-pdp-description__content--expanded");
        content.classList.remove("is-animating");
        this.classList.remove("vely-pdp-description--open");
        content.style.maxHeight = "";
      }
    };

    const clearTravelStyles = () => {
      if (toggle) {
        toggle.classList.remove("is-traveling");
        toggle.style.position = "";
        toggle.style.left = "";
        toggle.style.top = "";
        toggle.style.width = "";
        toggle.style.height = "";
        toggle.style.zIndex = "";
        toggle.style.margin = "";
        toggle.style.transform = "";
        toggle.style.transition = "";
      }
      this._spacer?.remove();
      this._spacer = null;
    };

    const setLabelState = (opened) => {
      if (!toggle) return;
      const moreEl = toggle.querySelector("[data-vely-toggle-more]");
      const lessEl = toggle.querySelector("[data-vely-toggle-less]");
      moreEl?.setAttribute("aria-hidden", opened ? "true" : "false");
      lessEl?.setAttribute("aria-hidden", opened ? "false" : "true");
    };

    const finishTravel = (token, opened) => {
      if (token !== this._animToken) return;
      this._animToken = 0;
      content.classList.remove("is-animating");
      clearTravelStyles();
      content.style.maxHeight = opened ? "none" : `${getLineHeight()}px`;
    };

    const runToggle = (opened) => {
      if (!toggle) {
        content.classList.toggle("vely-pdp-description__content--collapsed", !opened);
        content.classList.toggle("vely-pdp-description__content--expanded", opened);
        this.classList.toggle("vely-pdp-description--open", opened);
        return;
      }

      const token = ++this._animToken;
      content.classList.add("is-animating");
      const fromRect = toggle.getBoundingClientRect();
      const fromHeight = opened ? getLineHeight() : content.scrollHeight;

      if (opened) {
        content.classList.remove("vely-pdp-description__content--collapsed");
        content.classList.add("vely-pdp-description__content--expanded");
        this.classList.add("vely-pdp-description--open");
      } else {
        content.classList.add("vely-pdp-description__content--collapsed");
        content.classList.remove("vely-pdp-description__content--expanded");
        this.classList.remove("vely-pdp-description--open");
      }

      content.style.maxHeight = "none";
      const toHeight = opened ? content.scrollHeight : getLineHeight();
      const toRect = toggle.getBoundingClientRect();

      if (durationMs === 0) {
        finishTravel(token, opened);
        return;
      }

      const spacer = document.createElement("span");
      spacer.className = "vely-pdp-description__toggle-spacer";
      spacer.setAttribute("aria-hidden", "true");
      spacer.style.width = `${Math.ceil(toRect.width)}px`;
      spacer.style.height = `${Math.ceil(toRect.height)}px`;
      toggle.insertAdjacentElement("afterend", spacer);
      this._spacer = spacer;

      toggle.classList.add("is-traveling");
      toggle.style.position = "fixed";
      toggle.style.left = `${fromRect.left}px`;
      toggle.style.top = `${fromRect.top}px`;
      toggle.style.width = `${fromRect.width}px`;
      toggle.style.height = `${fromRect.height}px`;
      toggle.style.zIndex = "5";
      toggle.style.margin = "0";
      toggle.style.transform = "translate3d(0, 0, 0)";
      toggle.style.transition = "none";
      content.style.maxHeight = `${fromHeight}px`;
      content.offsetHeight;

      const dx = toRect.left - fromRect.left;
      const dy = toRect.top - fromRect.top;

      requestAnimationFrame(() => {
        if (token !== this._animToken) return;
        toggle.style.transition = `transform ${durationMs}ms ${ease}, width ${durationMs}ms ${ease}, height ${durationMs}ms ${ease}`;
        toggle.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
        toggle.style.width = `${toRect.width}px`;
        toggle.style.height = `${toRect.height}px`;
        content.style.maxHeight = `${toHeight}px`;
      });

      window.clearTimeout(this._animTimer);
      this._animTimer = window.setTimeout(() => finishTravel(token, opened), durationMs + 40);
    };

    if (expandEnabled) {
      if (content.classList.contains("vely-pdp-description__content--collapsed")) {
        content.style.maxHeight = `${getLineHeight()}px`;
      }
      syncToggleVisibility();
    }

    if (toggle && expandEnabled) {
      this._onToggle = () => {
        const opened = !isOpen();
        toggle.setAttribute("aria-expanded", opened ? "true" : "false");
        setLabelState(opened);
        runToggle(opened);
      };
      toggle.addEventListener("click", this._onToggle);
    }

    if (typeof ResizeObserver !== "undefined" && expandEnabled) {
      this._resizeObserver = new ResizeObserver(() => {
        if (this._animToken) return;
        if (isOpen()) {
          content.style.maxHeight = "none";
        } else {
          syncToggleVisibility();
          if (content.classList.contains("vely-pdp-description__content--collapsed")) {
            content.style.maxHeight = `${getLineHeight()}px`;
          }
        }
      });
      this._resizeObserver.observe(content);
    }
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
    window.clearTimeout(this._animTimer);
    this._spacer?.remove();
    if (this._toggle && this._onToggle) {
      this._toggle.removeEventListener("click", this._onToggle);
    }
  }
}

/**
 * @velyn-custom — gallery slide sync (hover-zoom removed by request).
 * Keeps main-slide srcset promotion in sync as Swiper changes the active
 * slide, and re-syncs once shortly after Core's init settles. The old
 * Drift hover-zoom src upgrading (data-vely-zoom-src / gallery_zoom_image)
 * was removed here since that markup is no longer emitted — Core's Drift
 * init in media-gallery.js now simply finds nothing to bind to.
 */
function observeVelyPdpGallerySlideChanges() {
  const wrapper = document.querySelector(
    ".sec__vely-main-product .media-main-swiper .swiper-wrapper"
  );
  if (!wrapper) return;

  promoteVelyPdpNearbyMainSlides(wrapper);
  scheduleVelyPdpFullGalleryPreload(wrapper);

  if (wrapper.dataset.velySlideObserved) return;
  wrapper.dataset.velySlideObserved = "true";

  new MutationObserver((mutations) => {
    const shouldSync = mutations.some(
      (mutation) =>
        mutation.type === "attributes" &&
        mutation.attributeName === "class" &&
        mutation.target instanceof Element &&
        mutation.target.classList.contains("swiper-slide")
    );
    if (shouldSync) promoteVelyPdpNearbyMainSlides(wrapper);
  }).observe(wrapper, {
    attributes: true,
    attributeFilter: ["class"],
    subtree: true,
  });
}

function scheduleVelyPdpGallerySyncAfterCoreInit() {
  setTimeout(() => {
    const wrapper = document.querySelector(
      ".sec__vely-main-product .media-main-swiper .swiper-wrapper"
    );
    promoteVelyPdpNearbyMainSlides(wrapper);
  }, 600);
}

function bindVelyPdpGallerySync() {
  observeVelyPdpGallerySlideChanges();
  scheduleVelyPdpGallerySyncAfterCoreInit();
}

/**
 * Far main slides use data-srcset without Core .image-lazy-load so
 * LazyLoadEventHover cannot blast them on first page interaction.
 * Promote only active ± 1 when Swiper updates slide classes.
 */
function promoteVelyPdpMainLazyImg(img) {
  if (!(img instanceof HTMLImageElement)) return;
  if (!img.hasAttribute("data-vely-pdp-main-lazy")) return;
  const srcset = img.getAttribute("data-srcset");
  if (!srcset) return;
  img.setAttribute("srcset", srcset);
  img.removeAttribute("data-srcset");
  img.classList.add("vely-pdp-main-lazy--loaded");
}

/**
 * @velyn-custom — same clone-timing problem as promoteVelyPdpMainLazyImg,
 * but for products with a video (or any other non-image media): those fall
 * back to Core media-gallery, whose slides use plain Core .image-lazy-load
 * (no data-vely-pdp-main-lazy). Core's site-wide LazyLoadEventHover upgrades
 * .image-lazy-load only once, on first page interaction — if Swiper's loop
 * mode clones the active slide (e.g. the video-thumbnail poster) after that
 * one-shot sweep already ran, the clone is stuck showing the 10px blur
 * placeholder forever. Promote it the same way, keyed off slide-class churn
 * we already observe.
 */
function promoteVelyPdpCoreLazyImg(img) {
  if (!(img instanceof HTMLImageElement)) return;
  const srcset = img.getAttribute("data-srcset");
  if (!srcset) return;
  img.setAttribute("srcset", srcset);
  img.removeAttribute("data-srcset");
  img.classList.add("lazy-loaded");
}

function promoteVelyPdpNearbyMainSlides(wrapper) {
  if (!wrapper) return;
  const slides = Array.from(wrapper.children).filter(
    (el) => el instanceof Element && el.classList.contains("swiper-slide")
  );
  if (!slides.length) return;

  let activeIndex = slides.findIndex((slide) =>
    slide.classList.contains("swiper-slide-active")
  );
  if (activeIndex < 0) activeIndex = 0;

  [activeIndex - 1, activeIndex, activeIndex + 1].forEach((index) => {
    if (index < 0 || index >= slides.length) return;
    slides[index]
      .querySelectorAll("img[data-vely-pdp-main-lazy][data-srcset]")
      .forEach(promoteVelyPdpMainLazyImg);
    slides[index]
      .querySelectorAll(".video-thumbnail img.image-lazy-load[data-srcset]")
      .forEach(promoteVelyPdpCoreLazyImg);
  });
}

/**
 * @velyn-custom — background preload for the rest of the gallery.
 * promoteVelyPdpNearbyMainSlides only upgrades active ± 1, so jumping to a
 * far thumbnail (e.g. slide 1 → slide 5) only starts that image's real
 * fetch on click, and Shopify CDN's first-time resize of an uncached width
 * can take a few seconds — showing the 10px placeholder stretched full-size
 * ("blurry photo") until it lands. Once the LCP-critical work is idle, we
 * quietly upgrade every remaining slide so later clicks/swipes hit the
 * browser cache instead of waiting on a fresh CDN fetch.
 */
function scheduleVelyPdpFullGalleryPreload(wrapper) {
  if (!wrapper || wrapper.dataset.velyFullPreloadScheduled) return;
  wrapper.dataset.velyFullPreloadScheduled = "true";

  const preloadAll = () => {
    wrapper
      .querySelectorAll("img[data-vely-pdp-main-lazy][data-srcset]")
      .forEach(promoteVelyPdpMainLazyImg);
    wrapper
      .querySelectorAll(".video-thumbnail img.image-lazy-load[data-srcset]")
      .forEach(promoteVelyPdpCoreLazyImg);
  };

  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(preloadAll, { timeout: 3000 });
  } else {
    setTimeout(preloadAll, 2000);
  }
}

function initVelyPdp() {
  bindVelyPdpGallerySync();

  if (!window.__velyPdpQuickViewModeBound) {
    window.__velyPdpQuickViewModeBound = true;
    document.addEventListener(
      "click",
      (event) => {
        const trigger = event.target.closest('[is="button-quick-view"]');
        if (!trigger) return;
        const drawer = document.querySelector("quickview-drawer");
        if (!drawer) return;
        if (trigger.hasAttribute("data-vely-details-only")) {
          drawer.setAttribute("data-vely-details-only", "true");
        } else {
          drawer.removeAttribute("data-vely-details-only");
        }
      },
      true
    );
  }

  bindGlobalAccessoryAtcSync();
  document
    .querySelectorAll("[data-vely-pdp-section]")
    .forEach(bindSection);
}

if (!customElements.get("vely-pdp-rotator")) {
  customElements.define("vely-pdp-rotator", VelyPdpRotator);
}
if (!customElements.get("vely-pdp-description")) {
  customElements.define("vely-pdp-description", VelyPdpDescription);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initVelyPdp);
} else {
  initVelyPdp();
}

document.addEventListener("shopify:section:load", initVelyPdp);

/** @velyn-custom END — vely-pdp-hero.js */
